import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { User, Branch, LoginCredentials, AuthContextType, UserPermissions } from '../types';
import { authService } from '../services/auth/auth.service';

const STORAGE_KEYS = {
  TOKEN: 'token',
  USER: 'user',
  COMPANY: 'selectedCompany',
  BRANCHES: 'branches',
  PERMISSIONS: 'userPermissions',
} as const;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEYS.TOKEN);
  });

  const [user, setUser] = useState<User | null>(() => {
    const savedUser = localStorage.getItem(STORAGE_KEYS.USER);
    return savedUser ? JSON.parse(savedUser) : null;
  });

  const [branches, setBranches] = useState<Branch[]>(() => {
    const savedBranches = localStorage.getItem(STORAGE_KEYS.BRANCHES);
    return savedBranches ? JSON.parse(savedBranches) : [];
  });
  
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(() => {
    const savedBranch = localStorage.getItem(STORAGE_KEYS.COMPANY);
    return savedBranch ? JSON.parse(savedBranch) : null;
  });

  const [permissions, setPermissions] = useState<UserPermissions | null>(() => {
    const savedPermissions = localStorage.getItem(STORAGE_KEYS.PERMISSIONS);
    return savedPermissions ? JSON.parse(savedPermissions) : null;
  });

  useEffect(() => {
    if (token) {
      localStorage.setItem(STORAGE_KEYS.TOKEN, token);
    } else {
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
    }
  }, [token]);

  useEffect(() => {
    if (user) {
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    } else {
      localStorage.removeItem(STORAGE_KEYS.USER);
    }
  }, [user]);

  useEffect(() => {
    if (selectedBranch) {
      localStorage.setItem(STORAGE_KEYS.COMPANY, JSON.stringify(selectedBranch));
    } else {
      localStorage.removeItem(STORAGE_KEYS.COMPANY);
    }
  }, [selectedBranch]);

  useEffect(() => {
    if (branches && branches.length > 0) {
      localStorage.setItem(STORAGE_KEYS.BRANCHES, JSON.stringify(branches));
    } else {
      localStorage.removeItem(STORAGE_KEYS.BRANCHES);
    }
  }, [branches]);

  useEffect(() => {
    if (permissions) {
      localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(permissions));
    } else {
      localStorage.removeItem(STORAGE_KEYS.PERMISSIONS);
    }
  }, [permissions]);

  // Auto-refresh permissions on mount if token exists but permissions are missing/stale
  useEffect(() => {
    if (token && (!permissions || Object.keys(permissions).length === 0)) {
      console.log('[AuthContext] Token exists but permissions missing/empty — auto-refreshing...');
      refreshPermissions().catch((err) => {
        console.error('[AuthContext] Auto-refresh permissions failed:', err);
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (credentials: LoginCredentials) => {
    try {
      const response = await authService.login(credentials);
      setToken(response.token);
      setUser(response.user);
      setBranches(response.branches);
      
      // Load user permissions after successful login
      if (response.token && response.user) {
        try {
          const userPermissions = await authService.getUserPermissions(response.token);
          console.log('[AuthContext] Permissions loaded on login:', userPermissions, 'Keys:', Object.keys(userPermissions || {}));
          setPermissions(userPermissions);
        } catch (permError) {
          console.error('[AuthContext] Error loading permissions on login:', permError);
          // Continue without permissions if there's an error
        }
      }
      
      return response;
    } catch (error) {
      console.error('Error during login:', error);
      throw error;
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setBranches([]);
    setSelectedBranch(null);
    setPermissions(null);
    Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
  };

  const selectBranch = async (branch: Branch) => {
    if (!token) {
      throw new Error('No authentication token found');
    }
    try {
      await authService.selectBranch(branch.id, token);
      setSelectedBranch(branch);
    } catch (error) {
      console.error('Error selecting branch:', error);
      throw error;
    }
  };

  const refreshPermissions = async () => {
    if (!token) {
      throw new Error('No authentication token found');
    }
    try {
      const userPermissions = await authService.getUserPermissions(token);
      setPermissions(userPermissions);
      console.log('Permissions refreshed:', userPermissions);
      return userPermissions;
    } catch (error) {
      console.error('Error refreshing permissions:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        branches,
        selectedBranch,
        permissions,
        isAuthenticated: !!token && !!user,
        login,
        logout,
        selectBranch,
        refreshPermissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
